import { api } from './api';

/** Why the browser cannot receive push, or null when it can. */
export type PushBlocker =
  | 'unsupported' // no service worker or no PushManager at all
  | 'ios-not-installed' // iOS only delivers push to a PWA on the home screen
  | 'denied' // the user refused the permission prompt
  | 'not-configured'; // the server has no VAPID key pair

export type PushState = {
  supported: boolean;
  blocker: PushBlocker | null;
  subscribed: boolean;
  /** The endpoint of this browser's subscription, needed to unsubscribe or test. */
  endpoint: string | null;
};

function isIos(): boolean {
  // iPadOS reports itself as a Mac, so the touch-point check catches it too.
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  // The iOS-only navigator.standalone is the reliable signal there; the media query
  // covers the installed PWA on every other engine.
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
  return legacy === true || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * iOS/iPadOS only exposes PushManager to a web app launched from the home screen.
 * In a Safari tab the API is simply absent, which is indistinguishable from an old
 * browser unless we check for iOS explicitly — and "your browser does not support
 * notifications" would send the user looking in entirely the wrong place.
 */
export function detectBlocker(): PushBlocker | null {
  if (isIos() && !isStandalone()) return 'ios-not-installed';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return null;
}

// VAPID public keys travel as base64url; PushManager wants raw bytes.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  // The backing buffer is allocated explicitly: a bare `new Uint8Array(n)` is typed
  // Uint8Array<ArrayBufferLike>, which is not assignable to BufferSource.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function currentSubscription(): Promise<globalThis.PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function readPushState(): Promise<PushState> {
  const blocker = detectBlocker();
  if (blocker) return { supported: false, blocker, subscribed: false, endpoint: null };
  try {
    const { publicKey } = await api.getPushPublicKey();
    if (!publicKey) {
      return { supported: false, blocker: 'not-configured', subscribed: false, endpoint: null };
    }
    const sub = await currentSubscription();
    return {
      supported: true,
      blocker: null,
      subscribed: sub !== null,
      endpoint: sub?.endpoint ?? null,
    };
  } catch {
    return { supported: false, blocker: 'not-configured', subscribed: false, endpoint: null };
  }
}

/**
 * Subscribes this browser and registers it with the server.
 *
 * Must be called straight from a user gesture: iOS rejects a permission request that
 * is not tied to a tap, and it does so permanently for that origin.
 */
export async function subscribeToPush(): Promise<PushState> {
  const blocker = detectBlocker();
  if (blocker) return { supported: false, blocker, subscribed: false, endpoint: null };

  const { publicKey } = await api.getPushPublicKey();
  if (!publicKey) {
    return { supported: false, blocker: 'not-configured', subscribed: false, endpoint: null };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { supported: true, blocker: 'denied', subscribed: false, endpoint: null };
  }

  // The worker is registered by vite-plugin-pwa's injected script; `ready` waits for
  // it rather than assuming it has already activated on a first visit.
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      // Chrome refuses a subscription that is not userVisibleOnly, and every push we
      // send does show a notification.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const raw = sub.toJSON();
  if (!raw.endpoint || !raw.keys?.p256dh || !raw.keys?.auth) {
    throw new Error('El navegador devolvió una suscripción incompleta');
  }
  await api.subscribePush({
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
  });
  return { supported: true, blocker: null, subscribed: true, endpoint: sub.endpoint };
}

/** Unsubscribes locally and drops the row server-side. */
export async function unsubscribeFromPush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  const { endpoint } = sub;
  await sub.unsubscribe().catch(() => {});
  // Best-effort: a row left behind is pruned on its next 410 anyway.
  await api.unsubscribePush(endpoint).catch(() => {});
}
